/**
 * Document Management Module - Service Layer
 * 
 * This service handles all business logic for document management including:
 * - Document CRUD operations
 * - Version management
 * - Approval workflow
 * - Compliance gap detection
 * - Notification generation
 * 
 * Now connected to real backend API
 */

import authService from '../../services/authService';
import {
  Document,
  DocumentMetadata,
  DocumentVersion,
  ApprovalRecord,
  ComplianceGap,
  ComplianceMapping,
  Notification,
  ControlRequirement,
  DocumentStatus,
  Framework,
  DocumentCategory,
  ComplianceStatus,
} from './types';

// Load BASE_URL from config
let BASE_URL = process.env.REACT_APP_API_URL;

async function loadConfig() {
  try {
    const publicUrl = process.env.PUBLIC_URL || '';
    const response = await fetch(`${publicUrl}/config.json`);
    const config = await response.json();
    BASE_URL = config.API_URL || BASE_URL;
  } catch (error) {
    console.error('Failed to load config.json. Using default .env API URL', error);
  }
}

loadConfig();

/**
 * Document Service
 * Connects to backend API for document operations
 */
class DocumentService {
  /**
   * Get all documents with optional filtering
   */
  async getDocuments(filters?: {
    category?: DocumentCategory;
    framework?: Framework;
    status?: DocumentStatus;
    searchTerm?: string;
  }): Promise<Document[]> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.framework) queryParams.append('framework', filters.framework);
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.searchTerm) queryParams.append('search', filters.searchTerm);

      const url = `${BASE_URL}/documents${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await authService.apiCall(url, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.statusText}`);
      }

      const data = await response.json();
      // Map API response to Document[] format and convert date strings to Date objects
      const documents = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
      return documents.map((doc: any) => this.parseDocumentDates(doc));
    } catch (error: any) {
      console.error('Error fetching documents:', error);
      throw new Error(error.message || 'Failed to fetch documents');
    }
  }

  /**
   * Parse date strings in document to Date objects
   */
  private parseDocumentDates(doc: any): Document {
    const metadata = doc.metadata || {};
    const parsedMetadata = {
      ...metadata,
      expiryDate: metadata.expiryDate ? new Date(metadata.expiryDate) : undefined,
      folderId: metadata.folderId || undefined,
      createdAt: metadata.createdAt ? new Date(metadata.createdAt) : new Date(),
      updatedAt: metadata.updatedAt ? new Date(metadata.updatedAt) : new Date(),
    };

    const parsedVersions = (doc.versions || []).map((v: any) => {
      return {
        ...v,
        uploadedAt: v.uploadedAt ? new Date(v.uploadedAt) : new Date(),
        modifiedAt: v.modifiedAt ? new Date(v.modifiedAt) : undefined,
      };
    });

    const parsedApprovals = (doc.approvals || []).map((a: any) => {
      return {
        ...a,
        submittedAt: a.submittedAt ? new Date(a.submittedAt) : new Date(),
        reviewedAt: a.reviewedAt ? new Date(a.reviewedAt) : undefined,
        approvedAt: a.approvedAt ? new Date(a.approvedAt) : undefined,
        publishedAt: a.publishedAt ? new Date(a.publishedAt) : undefined,
      };
    });

    return {
      ...doc,
      metadata: parsedMetadata,
      versions: parsedVersions,
      approvals: parsedApprovals,
    };
  }

  /**
   * Get a single document by ID
   */
  async getDocumentById(id: string): Promise<Document | null> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/${id}`, {
        method: 'GET',
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        // Try to get error message from response body
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to fetch document: ${response.statusText}`);
        } catch {
          throw new Error(`Failed to fetch document: ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      // Check if backend returned success: false
      if (data.success === false) {
        throw new Error(data.message || 'Failed to fetch document');
      }
      
      const document = data.data || data;
      return document ? this.parseDocumentDates(document) : null;
    } catch (error: any) {
      console.error('Error fetching document:', error);
      throw new Error(error.message || 'Failed to fetch document');
    }
  }

  /**
   * Upload a new document version
   */
  async uploadDocumentVersion(
    documentId: string,
    file: File,
    changeDescription?: string
  ): Promise<DocumentVersion> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (changeDescription) {
        formData.append('changeDescription', changeDescription);
      }

      const response = await authService.apiCall(`${BASE_URL}/documents/${documentId}/versions`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to upload version: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error: any) {
      console.error('Error uploading version:', error);
      throw new Error(error.message || 'Failed to upload document version');
    }
  }

  /**
   * Create a new document
   */
  async createDocument(
    metadata: Omit<DocumentMetadata, 'id' | 'createdAt' | 'updatedAt' | 'currentVersion'> & {
      documentOwner?: string;
      documentOwnerName?: string;
    },
    file: File
  ): Promise<Document> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Append all metadata fields
      formData.append('title', metadata.title);
      if (metadata.description) formData.append('description', metadata.description);
      formData.append('category', metadata.category);
      if (metadata.framework) formData.append('framework', metadata.framework);
      if (metadata.controlId) formData.append('controlId', metadata.controlId);
      if (metadata.riskLevel) formData.append('riskLevel', metadata.riskLevel);
      // documentOwner and documentOwnerName are optional - backend will use authenticated user
      if (metadata.documentOwner) formData.append('documentOwner', metadata.documentOwner);
      if (metadata.documentOwnerName) formData.append('documentOwnerName', metadata.documentOwnerName);
      if (metadata.folderId) formData.append('folderId', metadata.folderId);
      formData.append('status', metadata.status);
      if (metadata.expiryDate) {
        formData.append('expiryDate', metadata.expiryDate.toISOString());
      }
      if (metadata.tags && metadata.tags.length > 0) {
        formData.append('tags', JSON.stringify(metadata.tags));
      }

      const response = await authService.apiCall(`${BASE_URL}/documents`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create document: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error: any) {
      console.error('Error creating document:', error);
      throw new Error(error.message || 'Failed to create document');
    }
  }

  /**
   * Update document metadata
   */
  async updateDocument(
    documentId: string,
    metadata: Partial<DocumentMetadata>
  ): Promise<Document> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/${documentId}`, {
        method: 'PATCH',
        body: JSON.stringify(metadata),
      });

      if (!response.ok) {
        throw new Error(`Failed to update document: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error: any) {
      console.error('Error updating document:', error);
      throw new Error(error.message || 'Failed to update document');
    }
  }

  /**
   * Delete a document
   */
  async deleteDocument(documentId: string): Promise<void> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/${documentId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.statusText}`);
      }
    } catch (error: any) {
      console.error('Error deleting document:', error);
      throw new Error(error.message || 'Failed to delete document');
    }
  }

  /**
   * Submit document for review
   */
  async submitForReview(documentId: string): Promise<ApprovalRecord> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/${documentId}/submit-review`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Failed to submit for review: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error: any) {
      console.error('Error submitting for review:', error);
      throw new Error(error.message || 'Failed to submit document for review');
    }
  }

  /**
   * Review a document (Reviewer action)
   */
  async reviewDocument(
    documentId: string,
    approved: boolean,
    comments?: string
  ): Promise<ApprovalRecord> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/${documentId}/review`, {
        method: 'POST',
        body: JSON.stringify({ approved, comments }),
      });

      if (!response.ok) {
        throw new Error(`Failed to review document: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error: any) {
      console.error('Error reviewing document:', error);
      throw new Error(error.message || 'Failed to review document');
    }
  }

  /**
   * Approve a document (Approver action)
   */
  async approveDocument(documentId: string): Promise<ApprovalRecord> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/${documentId}/approve`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Failed to approve document: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error: any) {
      console.error('Error approving document:', error);
      throw new Error(error.message || 'Failed to approve document');
    }
  }

  /**
   * Publish a document (Compliance Officer action)
   */
  async publishDocument(documentId: string): Promise<ApprovalRecord> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/${documentId}/publish`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Failed to publish document: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error: any) {
      console.error('Error publishing document:', error);
      throw new Error(error.message || 'Failed to publish document');
    }
  }

  /**
   * Get compliance gaps
   */
  async getComplianceGaps(): Promise<ComplianceGap[]> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/compliance/gaps`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch compliance gaps: ${response.statusText}`);
      }

      const data = await response.json();
      return Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Error fetching compliance gaps:', error);
      throw new Error(error.message || 'Failed to fetch compliance gaps');
    }
  }

  /**
   * Get compliance mappings
   */
  async getComplianceMappings(): Promise<ComplianceMapping[]> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/compliance/mappings`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch compliance mappings: ${response.statusText}`);
      }

      const data = await response.json();
      return Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Error fetching compliance mappings:', error);
      throw new Error(error.message || 'Failed to fetch compliance mappings');
    }
  }

  /**
   * Get control requirements
   */
  async getControlRequirements(): Promise<ControlRequirement[]> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/compliance/controls`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch control requirements: ${response.statusText}`);
      }

      const data = await response.json();
      return Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Error fetching control requirements:', error);
      throw new Error(error.message || 'Failed to fetch control requirements');
    }
  }

  /**
   * Detect compliance gaps for a specific control
   */
  async detectGapsForControl(controlId: string): Promise<ComplianceGap[]> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/compliance/gaps/detect/${controlId}`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Failed to detect gaps: ${response.statusText}`);
      }

      const data = await response.json();
      return Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Error detecting gaps:', error);
      throw new Error(error.message || 'Failed to detect compliance gaps');
    }
  }

  /**
   * Get notifications
   */
  async getNotifications(unreadOnly?: boolean): Promise<Notification[]> {
    try {
      const url = `${BASE_URL}/notifications${unreadOnly ? '?unreadOnly=true' : ''}`;
      const response = await authService.apiCall(url, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch notifications: ${response.statusText}`);
      }

      const data = await response.json();
      const notifications = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
      return notifications.sort((a: Notification, b: Notification) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error: any) {
      console.error('Error fetching notifications:', error);
      throw new Error(error.message || 'Failed to fetch notifications');
    }
  }

  /**
   * Mark notification as read
   */
  async markNotificationAsRead(notificationId: string): Promise<void> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/notifications/${notificationId}/read`, {
        method: 'PATCH',
      });

      if (!response.ok) {
        throw new Error(`Failed to mark notification as read: ${response.statusText}`);
      }
    } catch (error: any) {
      console.error('Error marking notification as read:', error);
      throw new Error(error.message || 'Failed to mark notification as read');
    }
  }

  /**
   * Get compliance dashboard data
   */
  async getComplianceDashboard() {
    try {
      const response = await authService.apiCall(`${BASE_URL}/compliance/dashboard`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch dashboard data: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || data;
    } catch (error: any) {
      console.error('Error fetching dashboard:', error);
      throw new Error(error.message || 'Failed to fetch compliance dashboard');
    }
  }

  /**
   * Calculate compliance status for a control
   */
  async calculateControlComplianceStatus(controlId: string): Promise<ComplianceStatus> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/compliance/controls/${controlId}/status`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to calculate compliance status: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data?.status || data.status || 'Non-Compliant';
    } catch (error: any) {
      console.error('Error calculating compliance status:', error);
      return 'Non-Compliant';
    }
  }

  /**
   * Download document file
   */
  async downloadDocument(documentId: string, versionId?: string): Promise<Blob> {
    try {
      const url = versionId 
        ? `${BASE_URL}/documents/${documentId}/versions/${versionId}/download`
        : `${BASE_URL}/documents/${documentId}/download`;
      
      const response = await authService.apiCall(url, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to download document: ${response.statusText}`);
      }

      return await response.blob();
    } catch (error: any) {
      console.error('Error downloading document:', error);
      throw new Error(error.message || 'Failed to download document');
    }
  }

  /**
   * Get all standards/frameworks from the system
   */
  async getStandards(): Promise<Array<{ id: string; name: string; code?: string | null; type: string }>> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/compliance/standards`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch standards: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data || [];
    } catch (error: any) {
      console.error('Error fetching standards:', error);
      throw new Error(error.message || 'Failed to fetch standards');
    }
  }

  /**
   * Get controls with pagination (10 per page)
   */
  async getControls(page: number = 1, perPage: number = 10, query?: string): Promise<{
    data: Array<{ id: string; code: string; name: string; description?: string }>;
    totalCount: number;
    totalPages: number;
  }> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('pageNo', page.toString());
      queryParams.append('perPage', perPage.toString());
      if (query) {
        queryParams.append('query', query);
      }

      const response = await authService.apiCall(`${BASE_URL}/controls?${queryParams.toString()}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch controls: ${response.statusText}`);
      }

      const data = await response.json();
      // Map the backend response structure (capitalized keys) to frontend format
      const mappedControls = (data.data || []).map((control: any) => ({
        id: control.ID || control.id,
        code: control.Code || control.code || '',
        name: control.Name || control.name || '',
        description: control.Description || control.description || '',
      }));
      
      return {
        data: mappedControls,
        totalCount: data.totalCount || 0,
        totalPages: data.totalPages || 0,
      };
    } catch (error: any) {
      console.error('Error fetching controls:', error);
      throw new Error(error.message || 'Failed to fetch controls');
    }
  }

  /**
   * Get tree structure organized by folders
   */
  async getTreeStructure(filters?: {
    category?: DocumentCategory;
    framework?: Framework;
    status?: DocumentStatus;
    searchTerm?: string;
  }): Promise<{
    folders: Array<{
      id: string;
      name: string;
      path: string;
      parentId?: string;
      children: any[];
      expanded: boolean;
      isCustom: boolean;
    }>;
    totalDocuments: number;
    totalFolders: number;
    customFolders: number;
  }> {
    try {
      const queryParams = new URLSearchParams();
      if (filters?.category) queryParams.append('category', filters.category);
      if (filters?.framework) queryParams.append('framework', filters.framework);
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.searchTerm) queryParams.append('search', filters.searchTerm);

      const url = `${BASE_URL}/documents/tree${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await authService.apiCall(url, {
        method: 'GET',
      });

      if (!response.ok) {
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to fetch tree structure: ${response.statusText}`);
        } catch {
          throw new Error(`Failed to fetch tree structure: ${response.statusText}`);
        }
      }

      const data = await response.json();
      
      if (data.success === false) {
        throw new Error(data.message || 'Failed to fetch tree structure');
      }

      // Parse dates in the tree structure
      const parseTreeDates = (items: any[]): any[] => {
        return items.map(item => {
          if (item.children && Array.isArray(item.children)) {
            return {
              ...item,
              children: item.children.map((child: any) => {
                if (child.metadata) {
                  // It's a document
                  return this.parseDocumentDates(child);
                } else {
                  // It's a folder
                  return {
                    ...child,
                    children: parseTreeDates(child.children || []),
                  };
                }
              }),
            };
          }
          return item;
        });
      };

      const treeData = data.data || data;
      return {
        ...treeData,
        folders: parseTreeDates(treeData.folders || []),
      };
    } catch (error: any) {
      console.error('Error fetching tree structure:', error);
      throw new Error(error.message || 'Failed to fetch tree structure');
    }
  }

  /**
   * Get all custom folders for the current user
   */
  async getFolders(): Promise<Array<{
    id: string;
    name: string;
    path: string;
    parentId?: string;
    children: any[];
    expanded: boolean;
    isCustom: boolean;
    createdAt: Date;
    createdBy: string;
  }>> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/folders`, {
        method: 'GET',
      });

      if (!response.ok) {
        // Try to get error message from response body
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || `Failed to fetch folders: ${response.statusText}`);
        } catch {
          throw new Error(`Failed to fetch folders: ${response.statusText}`);
        }
      }

      const data = await response.json();
      console.log('Folders API response:', data); // Debug log
      
      // Check if backend returned success: false
      if (data.success === false) {
        throw new Error(data.message || 'Failed to fetch folders');
      }
      
      const folders = data.data || data || [];
      console.log('Parsed folders:', folders); // Debug log
      // Convert date strings to Date objects
      return folders.map((folder: any) => ({
        ...folder,
        createdAt: folder.createdAt ? new Date(folder.createdAt) : new Date(),
        children: folder.children || [],
      }));
    } catch (error: any) {
      console.error('Error fetching folders:', error);
      throw new Error(error.message || 'Failed to fetch folders');
    }
  }

  /**
   * Create a new custom folder
   */
  async createFolder(folderData: {
    name: string;
    path: string;
    parentId?: string;
  }): Promise<{
    id: string;
    name: string;
    path: string;
    parentId?: string;
    children: any[];
    expanded: boolean;
    isCustom: boolean;
    createdAt: Date;
    createdBy: string;
  }> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(folderData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create folder: ${response.statusText}`);
      }

      const data = await response.json();
      const folder = data.data || data;
      return {
        ...folder,
        createdAt: folder.createdAt ? new Date(folder.createdAt) : new Date(),
        children: [],
      };
    } catch (error: any) {
      console.error('Error creating folder:', error);
      throw new Error(error.message || 'Failed to create folder');
    }
  }

  /**
   * Update a custom folder
   */
  async updateFolder(
    id: string,
    folderData: {
      name?: string;
      path?: string;
      parentId?: string;
      expanded?: boolean;
    }
  ): Promise<{
    id: string;
    name: string;
    path: string;
    parentId?: string;
    children: any[];
    expanded: boolean;
    isCustom: boolean;
    createdAt: Date;
    createdBy: string;
  }> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/folders/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(folderData),
      });

      if (!response.ok) {
        throw new Error(`Failed to update folder: ${response.statusText}`);
      }

      const data = await response.json();
      const folder = data.data || data;
      return {
        ...folder,
        createdAt: folder.createdAt ? new Date(folder.createdAt) : new Date(),
        children: [],
      };
    } catch (error: any) {
      console.error('Error updating folder:', error);
      throw new Error(error.message || 'Failed to update folder');
    }
  }

  /**
   * Delete a custom folder
   */
  async deleteFolder(id: string): Promise<void> {
    try {
      const response = await authService.apiCall(`${BASE_URL}/documents/folders/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`Failed to delete folder: ${response.statusText}`);
      }
    } catch (error: any) {
      console.error('Error deleting folder:', error);
      throw new Error(error.message || 'Failed to delete folder');
    }
  }
}

// Export singleton instance
export const documentService = new DocumentService();
export default documentService;
